/**
 * Navigation Bar Component for You Don't Know You
 * 
 * Uses Clerk Authentication exclusively.
 */

import React from 'react';
import {
  SignInButton,
  UserButton,
  useUser
} from '@clerk/clerk-react';
import './Navbar.css';

/** Inline SVG logo — crisp on all screens including mobile retina */
export const YDKYLogo = () => (
  <svg
    height="32"
    viewBox="0 0 130 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="YDKY"
    className="ydky-logo-svg"
  >
    <defs>
      <linearGradient id="ydkyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#A855F7" />
        <stop offset="100%" stopColor="#EC4899" />
      </linearGradient>
    </defs>
    {/* Eye icon */}
    <path
      d="M3 16 C7 9 13 6 16 6 C19 6 25 9 29 16 C25 23 19 26 16 26 C13 26 7 23 3 16 Z"
      stroke="url(#ydkyGrad)"
      strokeWidth="2"
      fill="none"
    />
    <circle cx="16" cy="16" r="5" fill="url(#ydkyGrad)" opacity="0.9" />
    <circle cx="16" cy="16" r="2" fill="white" opacity="0.6" />
    {/* YDKY text */}
    <text
      x="38"
      y="23"
      fontFamily="'Inter', -apple-system, sans-serif"
      fontWeight="900"
      fontSize="19"
      letterSpacing="-0.5"
      fill="url(#ydkyGrad)"
    >
      YDKY
    </text>
  </svg>
);

/**
 * Navbar - Uses Clerk's built-in components
 */
export const Navbar = ({ onInviteFriend, onGoHome }) => {
  const { isSignedIn, isLoaded } = useUser();

  // Loading state
  if (!isLoaded) {
    return (
      <nav className="app-navbar">
        <div className="navbar-brand" onClick={onGoHome} style={{ cursor: onGoHome ? 'pointer' : 'default' }}>
          <YDKYLogo />
        </div>
        <div className="navbar-profile">
          <div className="navbar-icon navbar-loading">
            <div className="loading-pulse" />
          </div>
        </div>
      </nav>
    );
  }

  const handleBrandClick = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <nav className="app-navbar">
      <div className="navbar-brand" onClick={handleBrandClick} style={{ cursor: 'pointer' }}>
        <YDKYLogo />
      </div>
      <div className="navbar-profile">
        {isSignedIn ? (
          // Authenticated: Show Clerk's UserButton
          <div className="clerk-user-button-wrapper">
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: {
                    width: 40,
                    height: 40,
                  },
                  userButtonPopoverCard: {
                    background: 'rgba(30, 30, 50, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                  userButtonPopoverActionButton: {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  },
                  userButtonPopoverActionButtonText: {
                    color: 'rgba(255, 255, 255, 0.9)',
                  },
                  userButtonPopoverFooter: {
                    display: 'none',
                  },
                },
              }}
              userProfileMode="navigation"
              userProfileUrl="/profile"
            >
              {/* Custom menu items */}
              <UserButton.MenuItems>
                {onInviteFriend && (
                  <UserButton.Action
                    label="📧 Invite a Friend"
                    onClick={onInviteFriend}
                  />
                )}
              </UserButton.MenuItems>
            </UserButton>
          </div>
        ) : (
          // Not authenticated: Show Sign In button
          <div className="auth-buttons-container">
            <SignInButton mode="modal">
              <button className="navbar-signin-btn" type="button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span>Sign In</span>
              </button>
            </SignInButton>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;